import React, { useCallback, useEffect, useMemo, useState } from "react";
import { View } from "react-native";
import Animated, { useAnimatedStyle, useSharedValue, withSpring } from "react-native-reanimated";
import { useI18nMessage } from "@lookiero/i18n-react";
import { Button, Icon, Text } from "@lookiero/sty-psp-ui";
import { NextBestActionType } from "../../../../../../projection/nextBestActionDefinition/nextBestActionDefinition";
import {
  NextBestActionItem,
  NextBestActionCardStatus,
} from "../../../../components/nextBestActionItem/NextBestActionItem";
import { DOMAIN } from "../../../../i18n/i18n";
import { useNextBestAction } from "../../behaviours/useNextBestAction";
import { NextBestActionCardIcon } from "../nextBestActionCardIcon/NextBestActionCardIcon";
import { style as nextBestActionCardStyle } from "./NextBestActionCard.style";

const DEFAULT_STATUS_TIMEOUT = 2000;
const ICON_ANIMATION_MIN_SCALE = 0.8;

const NextBestActionCard: NextBestActionItem<NextBestActionType.CARD> = ({ action, segment }) => {
  const { actionStatuses, onActionTriggered } = useNextBestAction();
  const status = actionStatuses[action.id] || NextBestActionCardStatus.IDLE;

  const title = useI18nMessage({
    domain: DOMAIN,
    id: action.translationKey,
    values: { segment: segment.toLowerCase() },
  });
  const description = useI18nMessage({
    domain: DOMAIN,
    id: action.metadata.descriptionTranslationKey,
    values: { segment: segment.toLowerCase() },
  });
  const cta = useI18nMessage({
    domain: DOMAIN,
    id: action.metadata.ctaTranslationKey,
    values: { segment: segment.toLowerCase() },
  });

  const style = useMemo(() => nextBestActionCardStyle(), []);

  const handleOnCtaPress = useCallback(() => onActionTriggered?.({ action }), [action, onActionTriggered]);

  const [currentStatus, setCurrentStatus] = useState(status);
  useEffect(() => {
    setCurrentStatus(status);

    const initTimeout = () => setTimeout(() => setCurrentStatus(NextBestActionCardStatus.IDLE), DEFAULT_STATUS_TIMEOUT);

    if (status === NextBestActionCardStatus.IDLE) {
      return;
    }

    const timeout = initTimeout();

    return (): void => clearTimeout(timeout);
  }, [status]);

  const successIconAnimationValue = useSharedValue(currentStatus === NextBestActionCardStatus.IDLE ? 0 : 1);
  useEffect(() => {
    successIconAnimationValue.value = withSpring(currentStatus === NextBestActionCardStatus.IDLE ? 0 : 1);
  }, [currentStatus, successIconAnimationValue]);
  const successIconAnimatedStyles = useAnimatedStyle(
    () => ({
      transform: [
        {
          scale: ICON_ANIMATION_MIN_SCALE + (1 - ICON_ANIMATION_MIN_SCALE) * successIconAnimationValue.value,
        },
      ],
      opacity: successIconAnimationValue.value,
    }),
    [successIconAnimationValue],
  );

  const disabled = currentStatus === NextBestActionCardStatus.SUCCESS;

  return (
    <View style={style.card} testID={action.id}>
      <View style={style.content}>
        <View style={style.iconContainer}>
          <NextBestActionCardIcon icon={action.metadata.icon} />
          <Animated.View style={successIconAnimatedStyles} testID="next-best-action-card-success-icon">
            <Icon name="tick_fill" style={style.icon} />
          </Animated.View>
        </View>
        <Text level={3} style={style.title} variant="action">
          {title}
        </Text>
        <Text level={2} style={style.description} variant="detail">
          {description}
        </Text>
      </View>

      <Button
        disabled={disabled}
        size="small"
        style={{ button: style.cta }}
        testID="next-best-action-card-cta"
        onPress={handleOnCtaPress}
      >
        <Text level={3} style={[style.ctaText, disabled && style.ctaTextDisabled]} variant="action">
          {cta}
        </Text>
      </Button>
    </View>
  );
};

export { NextBestActionCard };
